import React, { useCallback, useMemo, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { FloatingPanel } from '@/UI/FloatingPanel/FloatingPanel';
import { TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useResolvedAssets } from '@/state/workflow.state';
import { InfoIcon } from '@/UI/Icons/InfoIcon';
import { XCircleOutlineIcon } from '@/UI/Icons/XCircleOutlineIcon';
import {
  CopyButton,
  CopyIndication,
  CustomChip,
  ExpandableText,
  ParamDisplay,
  SortedParams,
} from './ParamsDisplayComponents';
import { extractValue, processInputs, type ImageInput, type ProcessedInputs } from './galleryParamsUtils';
import ReferenceImages from './ReferenceImages';

const EMPTY_OBJECT = {};

interface GalleryParamsProps {
  inputs: Record<string, unknown>;
  container?: string;
  /** Lets a reference thumbnail start the same canvas drag as the asset preview. */
  onDragReferenceImage?: (event: DragEvent<HTMLElement>, image: ImageInput) => void;
  onDragReferenceImageEnd?: (event: DragEvent<HTMLElement>) => void;
}

function GalleryParams({ inputs, container = '', onDragReferenceImage, onDragReferenceImageEnd }: GalleryParamsProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { isHovered: isPromptHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleCloseParams = useCallback(() => {
    track('gallery_params_closed', {}, TrackTypeEnum.Product);
    setAnchorEl(null);
  }, [track]);

  const handleClickOpenParams = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      handleCloseParams();
      return;
    }

    setAnchorEl(event.currentTarget);
    track('gallery_params_opened', {}, TrackTypeEnum.Product);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'params-popover' : undefined;

  const resolvedAssets = useResolvedAssets();
  const { sortedParams, prompt, negativePrompt, imageInputs } = useMemo<Partial<ProcessedInputs>>(() => {
    if (inputs) {
      return processInputs(inputs, resolvedAssets);
    } else {
      return {
        sortedParams: undefined,
        prompt: undefined,
        negativePrompt: undefined,
        imageInputs: undefined,
      };
    }
  }, [inputs, resolvedAssets]);

  const { copyToClipboard: copyPromptToClipboard, copied: promptCopied } = useCopyToClipboard({
    trackingName: 'gallery_params_copied_prompt',
    trackingPayload: EMPTY_OBJECT,
  });
  const { copyToClipboard: copyNegativePromptToClipboard, copied: negativePromptCopied } = useCopyToClipboard({
    trackingName: 'gallery_params_copied_negative_prompt',
    trackingPayload: EMPTY_OBJECT,
  });
  const promptValue = extractValue(prompt);
  const negativePromptValue = extractValue(negativePrompt);

  if (container === 'gallery') {
    return (
      <FlexCol sx={{ gap: 1 }}>
        {promptValue && (
          <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} sx={{ mb: 2 }}>
            <FlexCenVer sx={{ justifyContent: 'space-between', height: '24px' }}>
              <TextSmRg>{t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.PROMPT)}</TextSmRg>
              {isPromptHovered && (
                <CopyButton text={promptValue} trackingName="gallery_params_copied_prompt" size="medium" />
              )}
            </FlexCenVer>
            <TextSmRg sx={{ mb: 2 }} color={token.textSecondary}>
              {promptValue}
            </TextSmRg>
          </Box>
        )}
        {imageInputs && imageInputs.length > 0 && (
          <ReferenceImages
            imageInputs={imageInputs}
            label={t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.REFERENCE_IMAGE, { count: imageInputs.length })}
            onDragStart={onDragReferenceImage}
            onDragEnd={onDragReferenceImageEnd}
          />
        )}
        <SortedParams params={sortedParams} RenderComponent={ParamDisplay} />
      </FlexCol>
    );
  }
  return (
    <>
      <TextSmRg
        onClick={handleClickOpenParams}
        sx={{
          width: 'fit-content',
          p: 1,
          ml: -1,
          cursor: 'pointer',
          gap: 0.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {open ? <XCircleOutlineIcon width={20} height={20} /> : <InfoIcon width={20} height={20} />}
        {t(
          open
            ? I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.HIDE_INFO
            : I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.SHOW_INFO,
        )}
      </TextSmRg>
      <FloatingPanel
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseParams}
        placement="bottom-start"
        offset={0}
        sx={{ width: '400px', background: color.Black100 }}
      >
        <FlexCol sx={{ gap: 1, width: '100%' }}>
          {promptValue && (
            <Box id="gallery-param-prompt" sx={{ position: 'relative' }}>
              <TextStdMd sx={{ mb: 1 }}>{t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.PROMPT)}</TextStdMd>
              <ExpandableText text={promptValue} />
              {/* support both prompt and prompt.value coming from LLM results */}
              <CopyIndication
                copied={promptCopied}
                copyToClipboard={() => promptValue && void copyPromptToClipboard(promptValue)}
              />
            </Box>
          )}
          {negativePromptValue && (
            <Box id="gallery-param-n-prompt" sx={{ position: 'relative' }}>
              <TextStdMd sx={{ my: 1 }}>{t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.NEGATIVE_PROMPT)}</TextStdMd>
              <ExpandableText text={negativePromptValue} />
              <CopyIndication
                copied={negativePromptCopied}
                copyToClipboard={() => negativePromptValue && void copyNegativePromptToClipboard(negativePromptValue)}
              />
            </Box>
          )}
          <SortedParams
            params={sortedParams}
            RenderComponent={CustomChip}
            header={<TextStdMd sx={{ my: 1 }}>{t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.PARAMETERS)}</TextStdMd>}
          />
        </FlexCol>
      </FloatingPanel>
    </>
  );
}

export default GalleryParams;
